import { AuditTrailEntry } from '../../../../services/workflow-engine/generated/models/audit-trail-entry';
import { WorkflowNode } from '../../../../services/workflow-engine/generated/models/workflow-node';
import { labelFor, roleLabel } from '../engine/engine-case.mapper';
import { AuditTrailAct, AuditTrailDay, AuditTrailView } from '../models/audit-trail-view';

export interface AuditTrailInput {
  entries: readonly AuditTrailEntry[];
  nodes: readonly WorkflowNode[];
  candidateTitles: Readonly<Record<string, string>>;
  /** Target role per action item id, when the host knows it. */
  itemRoles: Readonly<Record<string, string>>;
  nameOf: (oid: string | null | undefined) => string | null;
}

type JsonObject = Record<string, unknown>;

interface ActGroup {
  lead: AuditTrailEntry;
  followers: AuditTrailEntry[];
  at: { stepKey: string | null; itemId: string | null };
}

interface BuiltAct extends AuditTrailAct {
  isSystem: boolean;
  actorName: string;
  role: string | null;
  laneKey: string;
}

const OUTCOME_LABELS: Readonly<Record<string, string>> = {
  confirmed: 'Confirmed',
  dismissed: 'Dismissed',
  approved: 'Approved',
  returned: 'Returned',
  done: 'Done',
  not_applicable: 'Not applicable',
  cannot_complete: 'Cannot complete',
  all_done: 'All actions done',
  incomplete: 'Actions incomplete',
};

const FIELD_LABELS: Readonly<Record<string, string>> = {
  assessedAssessment: 'Assessment',
  selectedCandidate: 'Selected candidate',
  dismissReason: 'Reason for dismissing',
  reclassReason: 'Reason for reclassifying',
  claimReason: 'Reason for a different candidate',
  lpoCandidate: 'LPO candidate',
  externalRef: 'External reference',
};

const VOCABULARY_FIELDS = new Set(['assessment', 'assessedAssessment', 'disposition', 'validity', 'verdict']);
const REFERENCE_FIELDS = new Set(['selectedCandidate', 'selectedCandidates']);
const ROLE_FIELDS = new Set(['owner']);
const DATE_TIME_FIELDS = new Set(['time']);

function asObject(value: unknown): JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : {};
}

function payloadOf(entry: AuditTrailEntry): JsonObject {
  return asObject(entry.payload);
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function isLead(entry: AuditTrailEntry): boolean {
  return entry.eventKind === 'case-opened'
    || entry.eventKind === 'owner-claimed'
    || entry.eventKind === 'owner-released'
    || (entry.eventKind === 'step-entered' && entry.ownerOidAtTime !== null);
}

function outcomeLabel(token: string | null): string {
  return token ? OUTCOME_LABELS[token] ?? labelFor(token) : 'Unknown outcome';
}

function utcDate(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

function dateTimeValue(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  const wat = new Date(parsed.getTime() + 60 * 60 * 1000); // WAT is UTC+1, no DST
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const twoDigits = (part: number) => String(part).padStart(2, '0');
  return `${months[wat.getUTCMonth()]} ${wat.getUTCDate()}, ${twoDigits(wat.getUTCHours())}:${twoDigits(wat.getUTCMinutes())}:${twoDigits(wat.getUTCSeconds())} WAT`;
}

function valueText(value: unknown, candidateTitles: Readonly<Record<string, string>>, field = ''): string {
  if (Array.isArray(value)) {
    return value.map(item => valueText(item, candidateTitles, field)).join(', ');
  }
  if (value !== null && typeof value === 'object') {
    return Object.entries(value as JsonObject)
      .filter(([, nestedValue]) => nestedValue !== null && nestedValue !== '' && (!Array.isArray(nestedValue) || nestedValue.length > 0))
      .map(([name, nestedValue]) => `${FIELD_LABELS[name] ?? labelFor(name)}: ${valueText(nestedValue, candidateTitles, name)}`)
      .join('; ');
  }
  if (typeof value !== 'string') {
    return String(value);
  }
  if (REFERENCE_FIELDS.has(field) && candidateTitles[value]) {
    return candidateTitles[value];
  }
  if (DATE_TIME_FIELDS.has(field) && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    return dateTimeValue(value);
  }
  if (ROLE_FIELDS.has(field)) {
    return roleLabel(value);
  }
  return VOCABULARY_FIELDS.has(field) && !/^[A-Z0-9_-]+$/.test(value) ? labelFor(value) : value;
}

function buildActs(input: AuditTrailInput): BuiltAct[] {
  const nodes = new Map(input.nodes.map(node => [node.id, node]));
  const stepTitle = (key: string | null) => {
    if (!key) {
      return 'the case';
    }
    const title = nodes.get(key)?.title;
    return title || labelFor(key);
  };
  const stepRole = (key: string | null) => {
    if (!key) {
      return null;
    }
    const node = nodes.get(key);
    return node?.role ?? node?.body?.role ?? null;
  };
  const itemRole = (itemId: string) => input.itemRoles[itemId]
    ?? input.entries.find(entry => entry.eventKind === 'owner-claimed' && payloadOf(entry)['itemId'] === itemId)?.actorRole
    ?? null;
  const queueLine = (stepKey: string | null, itemId: string | null): string => {
    if (itemId) {
      const role = itemRole(itemId);
      return role ? `Action ${itemId} queued for ${roleLabel(role)}` : `Action ${itemId} queued`;
    }
    const node = stepKey ? nodes.get(stepKey) : undefined;
    const role = stepRole(stepKey);
    if (!node || node.kind === 'end' || node.kind === 'for-each' || !role || role.startsWith('item.')) {
      return `Moved to ${stepTitle(stepKey)}`;
    }
    return `Moved to ${stepTitle(stepKey)}, waiting in the ${roleLabel(role)} queue`;
  };
  const groups: ActGroup[] = [];
  let current = { stepKey: null as string | null, itemId: null as string | null };

  for (const entry of input.entries) {
    if (isLead(entry) || groups.length === 0) {
      groups.push({ lead: entry, followers: [], at: { ...current } });
    } else {
      groups[groups.length - 1].followers.push(entry);
    }
    if (entry.eventKind === 'case-opened') {
      current = { stepKey: entry.toStepKey, itemId: null };
    } else if (entry.eventKind === 'step-entered') {
      current = { stepKey: entry.toStepKey, itemId: stringValue(payloadOf(entry)['itemId']) };
    }
  }

  return groups.map(({ lead, followers, at }) => {
    const payload = payloadOf(lead);
    const capturedRecords = followers.filter(entry => entry.eventKind === 'step-record' && payloadOf(entry)['captures'] !== undefined);
    const firstCaptured = capturedRecords[0];
    const isSystem = lead.actorType !== 'human';
    const role = isSystem ? null : lead.actorRole;
    const actorName = isSystem ? 'IO (system)' : input.nameOf(lead.actorOid) ?? 'Unknown user';
    const act: BuiltAct = {
      id: lead.id,
      atUtc: lead.occurredOnUtc,
      headline: '',
      muted: lead.eventKind === 'owner-claimed' || lead.eventKind === 'owner-released',
      details: [],
      automatic: [],
      isSystem,
      actorName,
      role,
      laneKey: isSystem ? 'system' : `${lead.actorOid}|${role ?? ''}`,
    };

    if (typeof payload['reason'] === 'string' && payload['reason'].length > 0) {
      act.details.push({ label: 'Reason', text: payload['reason'] });
    }
    for (const record of capturedRecords) {
      const captures = asObject(payloadOf(record)['captures']);
      for (const fields of Object.values(captures)) {
        for (const [field, value] of Object.entries(asObject(fields))) {
          if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
            continue;
          }
          act.details.push({ label: FIELD_LABELS[field] ?? labelFor(field), text: valueText(value, input.candidateTitles, field) });
        }
      }
    }

    switch (lead.eventKind) {
      case 'case-opened':
        act.headline = 'Opened the case';
        act.automatic.push(queueLine(lead.toStepKey, null));
        break;
      case 'owner-claimed':
        act.headline = at.itemId ? `Claimed action ${at.itemId}` : `Claimed ${stepTitle(at.stepKey)}`;
        break;
      case 'owner-released': {
        const target = at.itemId ? `action ${at.itemId}` : stepTitle(at.stepKey);
        const ownerRole = at.itemId ? itemRole(at.itemId) : stepRole(at.stepKey);
        act.headline = ownerRole
          ? `Released ${target} back to the ${roleLabel(ownerRole)} queue`
          : `Released ${target}`;
        break;
      }
      case 'step-entered': {
        const decidedItemId = stringValue(payload['decidedItemId']);
        const itemOutcome = stringValue(firstCaptured?.outcomeToken) ?? lead.outcomeToken;
        if (decidedItemId) {
          act.headline = `Marked action ${decidedItemId} as ${outcomeLabel(itemOutcome)}`;
        } else if (lead.outcomeToken === 'returned') {
          act.headline = `Returned the case to ${stepTitle(lead.toStepKey)}`;
        } else {
          act.headline = `${stepTitle(lead.fromStepKey)}: ${outcomeLabel(lead.outcomeToken)}`;
        }

        if (lead.outcomeToken !== 'returned') {
          const destination = lead.toStepKey ? nodes.get(lead.toStepKey) : undefined;
          if (decidedItemId && destination?.kind === 'end') {
            act.automatic.push(`No actions left (${outcomeLabel(lead.outcomeToken)}); moved to ${stepTitle(lead.toStepKey)}`);
          } else {
            act.automatic.push(queueLine(lead.toStepKey, stringValue(payload['itemId'])));
          }
        }
        break;
      }
      default:
        act.headline = labelFor(lead.eventKind);
    }

    for (const follower of followers) {
      const followerPayload = payloadOf(follower);
      switch (follower.eventKind) {
        case 'step-record': {
          if (followerPayload['captures'] !== undefined) {
            break;
          }
          if (followerPayload['forEach'] !== undefined) {
            const items = stringList(followerPayload['items']);
            act.automatic.push(items.length === 0
              ? 'No recommended action applies to the selected candidates'
              : `${items.length} recommended action${items.length === 1 ? '' : 's'} ${items.length === 1 ? 'applies' : 'apply'} to the selected candidates: ${items.join(', ')}`);
          } else {
            act.automatic.push('Recorded step data');
          }
          break;
        }
        case 'step-entered':
          act.automatic.push(queueLine(follower.toStepKey, stringValue(followerPayload['itemId'])));
          break;
        case 'owner-returned': {
          const ownerName = input.nameOf(follower.ownerOidAtTime) ?? 'Unknown user';
          act.automatic.push(`Back at ${stepTitle(lead.toStepKey)} with ${ownerName}, who decided it last`);
          break;
        }
        case 'return-fallback':
          act.automatic.push(`Back at ${stepTitle(lead.toStepKey)}, waiting in the ${roleLabel(stepRole(lead.toStepKey))} queue (the person who decided it last is no longer available)`);
          break;
        case 'case-closed': {
          const ending = [lead, ...followers].filter(entry =>
            entry.eventKind === 'step-entered' && nodes.get(entry.toStepKey ?? '')?.kind === 'end',
          ).at(-1);
          const emptySelection = followers.some(entry =>
            entry.eventKind === 'step-record' && payloadOf(entry)['forEach'] !== undefined && stringList(payloadOf(entry)['items']).length === 0,
          );
          const closingOutcome = ending?.outcomeToken
            ? outcomeLabel(ending.outcomeToken)
            : emptySelection ? 'No actions to run' : outcomeLabel(follower.outcomeToken);
          act.automatic.push(`Case closed: ${closingOutcome}`);
          break;
        }
        default:
          act.automatic.push(labelFor(follower.eventKind));
      }
    }
    return act;
  });
}

export function buildAuditTrail(input: AuditTrailInput): AuditTrailView {
  const days: AuditTrailDay[] = [];
  const acts = buildActs(input);

  for (const act of acts) {
    const dateUtc = utcDate(act.atUtc);
    let day = days[days.length - 1];
    if (!day || day.dateUtc !== dateUtc) {
      day = { dateUtc, lanes: [] };
      days.push(day);
    }
    let lane = day.lanes[day.lanes.length - 1];
    if (!lane || lane.key !== act.laneKey) {
      lane = {
        key: act.laneKey,
        isSystem: act.isSystem,
        actorName: act.actorName,
        role: act.role,
        acts: [],
      };
      day.lanes.push(lane);
    }
    const viewAct: AuditTrailAct = {
      id: act.id,
      atUtc: act.atUtc,
      headline: act.headline,
      muted: act.muted,
      details: act.details,
      automatic: act.automatic,
    };
    lane.acts.push(viewAct);
  }

  return { days };
}